import { Plus, Sparkles } from "lucide-react";
import { useEffect, useMemo, useState, type FC } from "react";
import { Link } from "react-router-dom";

import { ForeignNationalDocumentsAPI } from "@/api/foreignNationalDocumentsApi";
import type { ClientCaseDocument } from "@/api/types/foreignNationalDocument";
import { FileTypeIcon } from "@/components/common/FileTypeIcon";
import { Surface, SurfaceHeader } from "@/components/surface";
import { Button } from "@/components/ui/button";

import { useClientProfile } from "../context/ClientProfileContext";

const READABLE_LABEL = /\b(cv|resume|résumé|diploma)\b/i;

/** Opens the autofill review, which reads CVs and diplomas from the client's cases. */
export const FillFromDocuments: FC<{
  variant?: "secondary" | "primary";
}> = ({ variant = "secondary" }) => {
  const { url } = useClientProfile();
  return (
    <Button
      variant={variant}
      data-testid="fill-from-documents"
      onClick={() => url.setTab("personal", { autofill: "1" })}
    >
      <Sparkles aria-hidden />
      Fill From CV and Diplomas
    </Button>
  );
};

/** The CVs and diplomas filed on the client's cases; null until loaded. */
const useReadableCaseFiles = (
  clientId: string
): ClientCaseDocument[] | null => {
  const [docs, setDocs] = useState<ClientCaseDocument[] | null>(null);
  useEffect(() => {
    let live = true;
    setDocs(null);
    ForeignNationalDocumentsAPI.listCaseDocuments(clientId)
      .then((r) => live && setDocs(r.documents))
      .catch(() => live && setDocs([]));
    return () => {
      live = false;
    };
  }, [clientId]);
  return useMemo(
    () =>
      docs
        ? docs.filter(
            (d) =>
              (d.kind === "education" || d.kind === "work") &&
              READABLE_LABEL.test(d.label)
          )
        : null,
    [docs]
  );
};

type AgentReadCardProps = {
  /** "Each address it finds…" */
  finds: string;
  addLabel: string;
  onAdd: () => void;
};

/**
 * Under an empty history table: the case files "Fill From CV and Diplomas"
 * would read, so the attorney knows what the agent has to work with. Hidden
 * when there are none.
 */
export const AgentReadCard: FC<AgentReadCardProps> = ({
  finds,
  addLabel,
  onAdd
}) => {
  const { clientId } = useClientProfile();
  const files = useReadableCaseFiles(clientId);
  if (!files || files.length === 0) return null;
  const cases = Array.from(
    new Set(files.flatMap((f) => f.cases.map((c) => c.case_label)))
  );
  const where =
    cases.length === 1 ? `the ${cases[0]} case` : `${cases.length} cases`;
  return (
    <Surface className="w-full text-left" data-testid="agent-read-card">
      <SurfaceHeader
        icon={Sparkles}
        title={`The agent can read ${files.length} ${
          files.length === 1 ? "file" : "files"
        } on ${where}`}
      />
      <ul className="divide-y divide-line-1">
        {files.map((f) => (
          <li key={f.id}>
            <Link
              to={f.path}
              className="flex min-w-0 items-start gap-2.5 px-4 py-2 hover:bg-fill-1"
            >
              <FileTypeIcon
                name={f.title}
                mimeType={f.mime_type ?? undefined}
                size={14}
                className="mt-0.5 shrink-0"
              />
              <span className="flex min-w-0 flex-col">
                <span className="truncate text-base text-fg">{f.title}</span>
                <span className="truncate text-sm text-fg-3">
                  {[f.label, f.cases.map((c) => c.case_label).join(", ")]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
              </span>
            </Link>
          </li>
        ))}
      </ul>
      <div className="flex items-center gap-2 border-t border-line-1 px-4 py-2">
        <span className="min-w-0 flex-1 text-sm text-fg-3">
          {finds} comes back as a change you approve.
        </span>
        <Button variant="ghost" onClick={onAdd}>
          <Plus />
          {addLabel}
        </Button>
        <FillFromDocuments variant="primary" />
      </div>
    </Surface>
  );
};
